import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseclient";

// Helper: calls your backend API route to actually send the invite email
async function sendInviteEmail(email, token, orgName) {
  try {
    const res = await fetch("/api/send-invite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, token, orgName }),
    });

    if (!res.ok) {
      console.error("Email sending failed, but invite row was created in the database.");
    }
  } catch (err) {
    console.error("sendInviteEmail error:", err);
    // Don't throw — the invite still exists in DB even if the email fails
  }
}

export function useOrganization(session) {
  const [organizations, setOrganizations] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const switchOrganization = useCallback(async (orgId) => {
    try {
      const { data: org } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", orgId)
        .single();

      setCurrentOrg(org);
      localStorage.setItem("currentOrgId", orgId);

      const { data: members } = await supabase
        .from("team_members")
        .select("*")
        .eq("organization_id", orgId)
        .eq("status", "active");

      setTeamMembers(members || []);
    } catch (err) {
      console.error("switchOrganization error:", err);
      setError(err.message);
    }
  }, []);

  const fetchOrganizations = useCallback(async () => {
    if (!session?.user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: err } = await supabase
        .from("user_organizations")
        .select("*")
        .eq("user_id", session.user.id);

      if (err) throw err;

      setOrganizations(data || []);

      const savedOrgId = localStorage.getItem("currentOrgId");
      const defaultOrg = data?.find((o) => o.id === savedOrgId) || data?.[0];

      if (defaultOrg) {
        await switchOrganization(defaultOrg.id);
      }
    } catch (err) {
      console.error("fetchOrganizations error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id, switchOrganization]);

  const inviteTeamMember = useCallback(
    async (email, role, designation = null) => {
      if (!currentOrg?.id) throw new Error("No organization selected");

      const normalizedEmail = email.trim().toLowerCase();

      // ✅ Check therapist limit before inviting
      const { data: orgData } = await supabase
        .from("organizations")
        .select("therapist_limit")
        .eq("id", currentOrg.id)
        .single();

      if (orgData?.therapist_limit !== null) {
        const activeCount = teamMembers.filter((m) => m.status === "active").length;
        if (activeCount >= orgData.therapist_limit) {
          throw new Error(
            `Therapist limit reached (${orgData.therapist_limit}). Upgrade your plan to add more team members.`
          );
        }
      }

      const token = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      // ✅ Upsert: insert new invite OR overwrite existing one for same email+org
      // This allows unlimited re-inviting without "duplicate key" errors
      const { data, error: err } = await supabase
        .from("team_invitations")
        .upsert(
          {
            organization_id: currentOrg.id,
            email: normalizedEmail,
            role,
            designation,
            invited_by: session.user.id,
            token,
            expires_at: expiresAt.toISOString(),
            status: "pending",
          },
          { onConflict: "organization_id,email" }
        )
        .select()
        .single();

      if (err) throw err;

      // ✅ Actually send the invite email
      await sendInviteEmail(normalizedEmail, token, currentOrg.name);

      return data;
    },
    [currentOrg?.id, session?.user?.id, teamMembers]
  );

  const acceptInvitation = useCallback(
    async (token) => {
      const { data: invitation, error: fetchErr } = await supabase
        .from("team_invitations")
        .select("*")
        .eq("token", token)
        .single();

      if (fetchErr || !invitation) throw new Error("Invalid invitation");
      if (invitation.status !== "pending") throw new Error("Invitation already used");
      if (new Date(invitation.expires_at) < new Date()) throw new Error("Invitation expired");

      const { error: insertErr } = await supabase.from("team_members").insert([{
        organization_id: invitation.organization_id,
        user_id: session.user.id,
        email: session.user.email,
        full_name: session.user.user_metadata?.full_name || "New Member",
        role: invitation.role,
        designation: invitation.designation,
        status: "active",
      }]);

      if (insertErr) throw insertErr;

      await supabase
        .from("team_invitations")
        .update({ status: "accepted" })
        .eq("token", token);

      await fetchOrganizations();
      return true;
    },
    [session?.user?.id, fetchOrganizations]
  );

  const updateTeamMember = useCallback(
    async (userId, role, designation = null) => {
      if (!currentOrg?.id) throw new Error("No organization selected");

      const { error: err } = await supabase
        .from("team_members")
        .update({ role, designation })
        .eq("organization_id", currentOrg.id)
        .eq("user_id", userId);

      if (err) throw err;
      await switchOrganization(currentOrg.id);
    },
    [currentOrg?.id, switchOrganization]
  );

  const removeTeamMember = useCallback(
    async (userId) => {
      if (!currentOrg?.id) throw new Error("No organization selected");

      const { error: err } = await supabase
        .from("team_members")
        .update({ status: "inactive" })
        .eq("organization_id", currentOrg.id)
        .eq("user_id", userId);

      if (err) throw err;
      await switchOrganization(currentOrg.id);
    },
    [currentOrg?.id, switchOrganization]
  );

  const hasPermission = useCallback(
    (permission) => {
      const me = teamMembers.find((m) => m.user_id === session?.user?.id);
      if (!me) return false;
      if (me.role === "admin") return true;
      return me.permissions?.[permission] === true;
    },
    [teamMembers, session?.user?.id]
  );

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  return {
    organizations,
    currentOrg,
    teamMembers,
    loading,
    error,
    switchOrganization,
    inviteTeamMember,
    acceptInvitation,
    updateTeamMember,
    removeTeamMember,
    hasPermission,
    refetch: fetchOrganizations,
  };
}